<script setup lang="ts">
import {
  DropdownMenuArrow,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from "reka-ui";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";

import { useRootLayout } from "@/hooks/use-root-layout";

import { caseService } from "@/services";
import { $can, fromUtc, toDuration } from "@/services/UtilitiesService";
import { useBrandSecurityStore, useBrandStore, useMessageStore } from "@/stores";

import { RsLoading, RsPagination } from "@/components";
import CaseDelete from "@/components/CaseDelete.vue";
import ColumnToggle from "@/components/ColumnToggle.vue";
import CaseSearchSortOrder from "@/components/CaseSearch/CaseSearchSortOrder.vue";
import CaseSearchDeselectFilters from "@/components/CaseSearch/CaseSearchDeselectFilters.vue";

import type { Column } from "@/models/Column";
import type { PagedData } from "@/models/PagedData";
import type { CaseSearchPersistedFilter, CaseSearchResult } from "@/models/CaseSearchResult";
import type { ChangePagination, DeleteFilter, SortOrder, ToggleSort } from "@/pages/CaseSearch.vue";

withDefaults(defineProps<{
  sortedColumn: SortOrder;
  result: PagedData<CaseSearchResult[]>;
  page: number;
  size: number;
  isLoading: boolean;
  currentError: string | undefined;
  searchKey: string;
  lastSearch: CaseSearchPersistedFilter | undefined;
  brandHiddenColumnsStorageKey: string;
}>(), {  })
defineEmits({
  changePagination: null as unknown as ChangePagination,
  refreshPage: null as unknown as () => void,
  toggleSort: null as unknown as ToggleSort,
  deleteFilter: null as unknown as DeleteFilter,
});

const router = useRouter();
const selectedBrand = computed(() => useBrandStore().selectedBrand);
const selectedClient = computed(() => useBrandStore().selectedClient);

const { layout } = useRootLayout();

const orderedColumns = computed(() => {
  const caseSearch = useBrandStore().caseSearchConfig;

  return caseSearch.filter((a) => a.visible !== false);
});

const nonHiddenColumns = computed(() => {
  return orderedColumns.value
    .filter((column) => !hiddenColumns.value.includes(column.fieldPath))
    .map((col) => ({ col, sortOrder: __props.sortedColumn?.path === col.fieldPath ? __props.sortedColumn.order : undefined }));
});

const sortableColumns = computed(() => {
  return orderedColumns.value.filter((col) => col.sortable);
});

const canDeleteCase = computed(() => $can("deleteCase"));

const canManageAccess = computed(
  () => useBrandSecurityStore().config?.isCaseSecurityEnabled === true && ($can("manageAccess") || $can("auditAccess")),
);

// const formatter = computed(() => {
//   const formatter = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });

//   const timezone = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
//     .formatToParts(new Date())
//     .find((p) => p.type === "timeZoneName")?.value;

//   return { formatter, timezone };
// });

const hiddenColumns = ref<string[]>(
  (() => {
    const stored = localStorage.getItem(__props.brandHiddenColumnsStorageKey);
    if (stored != null && stored !== "{}") return JSON.parse(stored) as string[];

    return orderedColumns.value.filter((col) => !col.defaultVisible).map((col) => col.fieldPath);
  })(),
);

const isDownloading = ref(false);

function getFieldPathValue(fieldPath: string, data: unknown) {
  // Paths after first two leaves are in a collection
  const [L1, L2, ...subs] = fieldPath.split("/");
  let res = (data as Record<string, Record<string, unknown>>)?.[L1];
  if (L2 != null) res = res?.[L2] as Record<string, unknown>;

  // Find sub-paths item in array
  if (subs.length > 0 && Array.isArray(res)) {
    return res.map((item) => subs.reduce((value, leaf) => value?.[leaf], item));
  }

  return res;
}

function renderPathValue(value: unknown, col: Column) {
  let values = (Array.isArray(value) ? value : [value]).filter((val) => val != null);

  switch (col.filterConfig?.type) {
    case "boolean": {
      const trueIndex = col.filterConfig.options.findIndex((opt) => opt.value);
      const falseIndex = col.filterConfig.options.findIndex((opt) => !opt.value);
      const { options } = col.filterConfig;

      values = values.flatMap((val) => (val ? options[trueIndex].label : options[falseIndex].label));
      break;
    }
    case "date-range": {
      values = values.flatMap((date) => {
        try {
          return fromUtc(date as string);
        } catch (e) {
          return date;
        }
      });
      break;
    }
    case "time-range": {
      values = values.flatMap((date) => toDuration((date as number) / 1e3));
      break;
    }
    case "select": {
      const options = col.filterConfig.options ?? [];

      values = values.flatMap((val) => {
        const option = options.find((opt) => opt.value === val);
        return option?.label ?? val;
      });
      break;
    }
    default:
      values = values.flat();
      break;
  }

  return values.filter((val) => String(val) !== "").join(", ");
}

function goToCase(casename: string) {
  if (selectedBrand.value == null) return;
  router.push({ name: "case", params: { brand: selectedBrand.value, casename } });
}

function updateHiddenColumns(state: string[]) {
  hiddenColumns.value = state;
  localStorage.setItem(__props.brandHiddenColumnsStorageKey, JSON.stringify(state));
}

async function startCSVDownload() {
  if (selectedClient.value == null || selectedBrand.value == null) return;

  try {
    if (__props.lastSearch == null) throw new Error("No search body found for CSV download");
    const { rawFilters: _, ...body } = __props.lastSearch;

    isDownloading.value = true;
    const csvString = await caseService.DownloadCSV(selectedClient.value, selectedBrand.value, {
      ...body,
      visibleFields: nonHiddenColumns.value.map(({ col }) => col.fieldPath),
    });

    const link = Object.assign(document.createElement("a"), {
      download: `Cases (${new Date()
        .toISOString()
        .replace(/(?<=\d)T(?=\d+:)/, " ")
        .replace(/\.\d{3}Z/, "")}).csv`,
      href: URL.createObjectURL(new Blob([csvString], { type: "text/csv" })),
    });

    link.click();
  } catch (error) {
    useMessageStore().updateMessage({
      type: "error",
      message: error instanceof Error ? error.message : "Error requesting CSV for download.",
      autoHide: true,
      showReload: false,
      title: "CSV Download",
    });
  } finally {
    isDownloading.value = false;
  }
}
</script>
<template>
  <div
    class="tw-flex tw-min-h-0 tw-w-full tw-flex-1 tw-flex-col tw-gap-3 tw-overflow-y-auto tw-rounded-sm tw-bg-white tw-p-2"
  >
    <h2 class="tw-px-2 tw-text-lg tw-font-semibold tw-tracking-wide">Results</h2>
    <div class="tw-relative tw-flex tw-flex-wrap tw-items-end tw-justify-start tw-gap-2">
      <ColumnToggle :hidden="hiddenColumns" :columns="orderedColumns" @on-state-change="updateHiddenColumns" />
      <CaseSearchSortOrder
        v-if="sortableColumns.length > 0"
        :columns="sortableColumns"
        :sorted-column="sortedColumn"
        @toggle-sort="(order) => $emit('toggleSort', order)"
      />
      <button
        type="button"
        :disabled="nonHiddenColumns.length < 1"
        class="tw-btn-lg-gray tw-flex tw-min-w-max tw-items-center tw-justify-start tw-gap-2 disabled:tw-cursor-not-allowed disabled:tw-bg-gray-100 disabled:tw-text-gray-600"
        @click="startCSVDownload"
      >
        <template v-if="isDownloading">
          <i class="fas fa-circle-notch fa-spin fa-sm"></i>
          Preparing download
        </template>
        <template v-else>Download to CSV</template>
      </button>
      <CaseSearchDeselectFilters
        :key="searchKey"
        :last-search="lastSearch"
        @delete-filter="(...args) => $emit('deleteFilter', ...args)"
      />
      <div aria-hidden="true" class="tw-flex-1"></div>
      <RsPagination
        :page="page"
        :count="size"
        :result="result"
        @do-search="(page, size) => $emit('changePagination', { page, size })"
      />
    </div>
    <div
      tabindex="0"
      class="tw-grid tw-size-full tw-grow tw-place-items-center tw-overflow-x-auto"
      :class="{ 'tw-min-h-full': layout === 'FIXED' }"
    >
      <h1 class="tw-sr-only">Main Content</h1>
      <RsLoading v-if="isLoading" id="fetchingCases" message="Fetching cases" />
      <p
        v-else-if="currentError != null"
        role="status"
        aria-live="polite"
        class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-p-4"
      >
        <span class="tw-font-medium tw-underline tw-ring-offset-2"> Error fetching cases </span>
        <span class="tw-text-sm">({{ currentError }})</span>
      </p>
      <div
        v-else-if="result.items.length == 0"
        class="tw-grid tw-place-items-center tw-p-4 tw-font-medium"
        aria-label="Search query did not match any cases. No data to display."
      >
        No results found
      </div>
      <div
        v-else-if="nonHiddenColumns.length == 0"
        class="tw-grid tw-place-items-center tw-p-4 tw-font-medium"
        aria-label="All columns have been hidden. No data to display."
      >
        All columns hidden
      </div>
      <table v-else aria-label="Search Results" class="tw-w-full tw-self-start tw-rounded-md tw-border">
        <thead>
          <tr
            tabindex="0"
            aria-label="Header / Column Names"
            class="z-1 tw-sticky -tw-top-1 tw-h-9 tw-divide-x tw-border-b tw-bg-gray-100 [&>*]:tw-p-4"
          >
            <th
              v-for="{ col, sortOrder } in nonHiddenColumns"
              :key="col.fieldPath"
              scope="col"
              tabindex="0"
              class="tw-h-7 tw-min-w-32 tw-max-w-sm"
              :aria-sort="
                !col.sortable || sortOrder == null ? 'none' : sortOrder === 'asc' ? 'ascending' : 'descending'
              "
            >
              <div class="tw-flex tw-justify-between">
                <span>{{ col.displayName }}</span>
                <button
                  v-if="col.sortable && sortOrder != null"
                  type="button"
                  :title="`Sorted ${
                    sortOrder === 'asc'
                      ? col.sortConfig?.asc == null
                        ? 'in ascending'
                        : `from ${col.sortConfig.asc}`
                      : col.sortConfig?.desc == null
                        ? 'in descending'
                        : `from ${col.sortConfig.desc}`
                  } order`"
                >
                  <i
                    class="fa"
                    aria-hidden="true"
                    :class="{
                      'fa-arrow-down-a-z': sortOrder === 'asc',
                      'fa-arrow-down-z-a': sortOrder === 'desc',
                    }"
                  ></i>
                </button>
              </div>
            </th>
            <th v-if="canManageAccess || canDeleteCase" class="tw-sticky tw-right-0 tw-w-px">
              <span class="tw-sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, idx) in result.items"
            :key="item.id"
            :tabindex="0"
            :class="{ 'tw-bg-gray-200': idx % 2 === 0 }"
            :aria-describedby="`ClickableRow_${encodeURIComponent(item.caseReference)}`"
            class="tw-h-7 tw-cursor-pointer tw-divide-x tw-border-b last:tw-border-b-0 hover:tw-bg-gray-300 focus:tw-bg-gray-300 [&>*]:tw-p-2"
            @keyup="(e) => e.key === 'Enter' && goToCase(item.caseReference)"
          >
            <td class="tw-hidden">
              <p :id="`ClickableRow_${encodeURIComponent(item.caseReference)}`">
                Press the Enter key or Click any cell in this row to go to case "{{ item.caseReference }}"
              </p>
            </td>
            <td
              v-for="{ col } in nonHiddenColumns"
              :key="col.fieldPath"
              tabindex="0"
              class="tw-break-words"
              :title="`Go to Case ${item.caseReference}`"
              @click="() => goToCase(item.caseReference)"
            >
              <span
                v-for="value in [renderPathValue(getFieldPathValue(col.fieldPath, item), col)]"
                :key="value"
                class="tw-line-clamp-3 tw-max-w-80"
              >
                {{ value }}
              </span>
            </td>
            <td v-if="canManageAccess || canDeleteCase" title="Actions" @keyup.stop>
              <div class="tw-flex tw-items-center tw-justify-center">
                <DropdownMenuRoot>
                  <DropdownMenuTrigger class="tw-aspect-square tw-w-6 tw-rounded-full hover:tw-bg-gray-600/20">
                    <span class="tw-sr-only">Open Actions</span>
                    <i aria-hidden="true" class="fas fa-ellipsis-vertical" />
                  </DropdownMenuTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuContent
                      align="end"
                      class="tw-flex tw-w-full tw-max-w-[20rem] tw-flex-col tw-gap-0.5 tw-rounded-lg tw-border tw-bg-white tw-p-2"
                      @click.stop
                    >
                      <DropdownMenuArrow class="tw-fill-white" />
                      <DropdownMenuLabel
                        class="tw-truncate tw-border-b tw-text-[0.65rem] tw-font-medium tw-text-gray-900/80"
                      >
                        Case {{ item.caseReference }}
                      </DropdownMenuLabel>
                      <DropdownMenuItem as-child>
                        <router-link
                          v-if="canManageAccess"
                          class="outline-none tw-flex tw-cursor-pointer tw-items-center tw-justify-start tw-gap-2 tw-rounded-md tw-p-2 hover:tw-bg-gray-500/20"
                          :to="`/${selectedBrand}/access/${item.caseReference}`"
                        >
                          <i aria-hidden="true" class="fas fa-user-lock" />
                          View/Manage Case Access
                        </router-link>
                      </DropdownMenuItem>
                      <CaseDelete v-if="canDeleteCase" :casename="item.caseReference" @deleted="$emit('refreshPage')">
                        <DropdownMenuItem
                          class="outline-none tw-flex tw-cursor-pointer tw-items-center tw-justify-start tw-gap-2 tw-rounded-md tw-p-2 hover:tw-bg-gray-500/20"
                          @select.prevent
                        >
                          <i aria-hidden="true" class="fas fa-trash-can" />
                          Delete Case
                        </DropdownMenuItem>
                      </CaseDelete>
                    </DropdownMenuContent>
                  </DropdownMenuPortal>
                </DropdownMenuRoot>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
